import {EMPTY, expand, reduce} from 'rxjs';
import {Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild, WritableSignal, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {NgClass} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {CdkDrag, CdkDragDrop, CdkDropList} from '@angular/cdk/drag-drop';
import {AuthService} from '@services/auth.service';
import {BoosterService} from '@services/card-game/booster.service';
import {UserService} from '@services/user/user.service';
import {PredictionService} from '@services/prediction.service';
import {StatisticsService} from '@services/statistics.service';
import {ToastService} from '@services/toast.service';
import {User} from '@/app/core/models/user';
import {UserStatistics} from '@/app/core/models/stats';
import {Prediction} from '@/app/core/models/bet/prediction';
import {LocalizedDatePipe, PageSelectorComponent, TooltipDirective} from '@/app/shared';
import {environment} from '@/environments/environment';
import {TranslateDynamicService} from '@services/translate-dynamic.service';
import {animate, style, transition, trigger} from '@angular/animations';
import {CardComponent} from '@/app/shared/components/card/card.component';
import {Card, CardRarity} from '@/app/shared/components/card/card.model';
import {InventoryService} from '@services/user/inventory.service';
import {FeatureFlagService} from '@services/feature-flag.service';
import {CardDestroyResult, CardStats, Inventory, InventoryCard} from '@/app/core/models/inventory.model';
import {BoosterDisplayComponent} from '@/app/shared/components/booster-display/booster-display.component';
import {BoosterStateService} from '@services/card-game/booster-state.service';
import {BoosterOpeningComponent} from '@/app/pages/tcg/booster-opening/booster-opening';
import {CardService} from '@services/card-game/card.service';
import {FavoriteCard} from '@/app/core/models/collection/favorite-card.model';
import {PriceHistoryChartComponent} from '@/app/shared';
import {MarketplaceService} from '@services/card-game/marketplace.service';
import {SaleRecord} from '@/app/core/models/marketplace.model';
import {DiscordService} from '@services/discord.service';
import {DiscordLinkStatus} from '@/app/core/models/discord';
import {boosterImageOrNull} from '@/app/shared/utils/booster-image';
import {Extension} from '@/app/core/models/collection/extension';
import {ExtensionService} from '@services/card-game/extension.service';

export type ProfileTab = 'inventory' | 'favorites' | 'predictions' | 'achievements' | 'account';

/** Carte possédée + son score — le score vit à côté de la carte, pas dedans. */
/** Exemplaire du pool de favoris : `remaining` = unités pas encore placées dans un slot. */
export interface PoolCard extends OwnedCard {
	remaining: number;
}

export interface OwnedCard {
	/** Id de la ligne user_cards (exemplaire précis) — clé attendue par l'API favoris/marketplace. */
	userCardId: string;
	card: Card;
	score: number;
}

const FAVORITE_SLOT_COUNT = 5;

@Component({
	selector: 'peps-profile',
	imports: [TranslatePipe, NgClass, LocalizedDatePipe, PageSelectorComponent, CardComponent, BoosterDisplayComponent, TooltipDirective, BoosterOpeningComponent, CdkDropList, CdkDrag, PriceHistoryChartComponent],
	templateUrl: './profile.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [
		trigger('fadeInOut', [
			transition(':enter', [
				style({ opacity: 0 }),
				animate('120ms ease-out', style({ opacity: 1 }))
			]),
			transition(':leave', [
				animate('100ms ease-in', style({ opacity: 0 }))
			])
		]),
		trigger('scaleInOut', [
			transition(':enter', [
				style({ transform: 'scale(0.8)' }),
				animate('120ms ease-out', style({ transform: 'scale(1)' }))
			]),
			transition(':leave', [
				animate('100ms ease-in', style({ transform: 'scale(0.8)' }))
			])
		])
	]
})
export class ProfileComponent implements OnInit, OnDestroy {

	private readonly boosterService = inject(BoosterService);
	private readonly userService = inject(UserService);
	protected readonly authService = inject(AuthService);
	private readonly router = inject(Router);
	private readonly statisticsService = inject(StatisticsService);
	private readonly predictionService = inject(PredictionService);
	private readonly toastService = inject(ToastService);
	private readonly translate = inject(TranslateService);
	protected readonly translateDynamicService: TranslateDynamicService = inject(TranslateDynamicService);
	private readonly inventoryService = inject(InventoryService);
	private readonly boosterStateService = inject(BoosterStateService);
	private readonly cardService = inject(CardService);
	private readonly marketplaceService = inject(MarketplaceService);
	private readonly discordService = inject(DiscordService);
	private readonly activeRoute = inject(ActivatedRoute);
	protected readonly featureFlagService = inject(FeatureFlagService);
	private readonly extensionService = inject(ExtensionService);

	isLoading = signal(this.authService.isAuthenticated());
	user: WritableSignal<User | null> = signal(null);
	userStats: WritableSignal<UserStatistics | null> = signal(null);
	predictions = signal<Prediction[]>([]);
	selectedTab = signal<string>('inventory');
	isWantDeleteAccount = signal(false);
	discordStatus: WritableSignal<DiscordLinkStatus | null> = signal(null);
	isLinkingDiscord = signal(false);
	isUnlinkingDiscord = signal(false);
	inventory: WritableSignal<Inventory | null> = signal(null);
	isOpeningBooster = signal(false);
	/** Dernier type de booster ouvert : « ouvrir le suivant » reprend ce type tant qu'il en reste. */
	private lastOpenedBoosterId: string | null = null;
	/** Modal simple "voir en grand" — juste la carte, rien d'autre. */
	selectedCard = signal<OwnedCard | null>(null);

	/** Modal détail dédiée (bouton "œil") — historique des prix + détruire/vendre. Entrée complète (avec quantity). */
	viewingCard = signal<InventoryCard | null>(null);

	// ── Détail carte : historique des prix + détruire + mettre en vente ──
	detailSales = signal<SaleRecord[]>([]);
	isDetailSalesLoading = signal(false);
	detailSalesNotFound = signal(false);

	isDestroyConfirming = signal(false);
	isDestroying = signal(false);

	/**
	 * PepsCoins rendus par la destruction d'une carte, par rareté — piloté depuis le back-office
	 * (GET /cards/destroy-rewards). Rareté absente (ex. COLLECTOR) = indestructible → 0.
	 */
	destroyRewardByRarity = signal<Partial<Record<string, number>>>({});

	/** Montant rendu si la carte actuellement ouverte dans la modal détail est détruite (0 si indestructible). */
	destroyValue = computed(() => {
		const rarity = this.viewingCard()?.card.rarity;
		return rarity ? (this.destroyRewardByRarity()[rarity] ?? 0) : 0;
	});

	isSellPriceOpen = signal(false);
	sellPrice = signal(50);

	isListingFromDetail = signal(false);
	cardNameFilter = signal('');
	/** Le back ne filtre que sur une seule rareté à la fois (`GET /inventory/cards?rarity=`). */
	selectedRarity = signal<CardRarity | null>(null);
	selectedExtension = signal<string | null>(null);
	/** Tri de la grille inventaire : 'rarity' (palier de rareté puis score), 'name' (nom puis score) ou 'score'. */
	inventorySort = signal<'rarity' | 'name' | 'score'>('rarity');
	/** Sens du tri sélectionné ('rarity' : ordre de tier métier direct/inversé). */
	inventorySortDirection = signal<'asc' | 'desc'>('desc');

	/** Vidéo/cascade d'ouverture de booster en cours — sert à mettre la grille inventaire en pause. */
	isBoosterOpening = computed(() => this.boosterStateService.state() !== 'idle');

	extensions = signal<Extension[]>([]);

	readonly rarityOptions: { key: CardRarity; labelKey: string; color: string; bg: string }[] = [
		{ key: 'COMMON',    labelKey: 'page.profile.inventory.rarity-common',    color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
		{ key: 'RARE',      labelKey: 'page.profile.inventory.rarity-rare',      color: '#38bdf8', bg: 'rgba(56,189,248,0.12)'  },
		{ key: 'EPIC',      labelKey: 'page.profile.inventory.rarity-epic',      color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)'  },
		{ key: 'LEGENDARY', labelKey: 'page.profile.inventory.rarity-legendary', color: '#eab308', bg: 'rgba(234,179,8,0.12)'   },
		{ key: 'COLLECTOR', labelKey: 'page.profile.inventory.rarity-collector', color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
	];

	rarityLabelKey(rarity: CardRarity): string {
		return this.rarityOptions.find(r => r.key === rarity)?.labelKey ?? '';
	}

	cardStats = signal<CardStats | null>(null);

	// ── Cartes favorites ──
	readonly favoritePoolListId = 'fav-pool';
	readonly favoriteSlotListIds = Array.from({length: FAVORITE_SLOT_COUNT}, (_, i) => `fav-slot-${i}`);
	readonly favoriteDropListIds = [this.favoritePoolListId, ...this.favoriteSlotListIds];

	favoriteSlots = signal<(OwnedCard | null)[]>(new Array(FAVORITE_SLOT_COUNT).fill(null));
	private savedFavoriteIds = signal<(string | null)[]>(new Array(FAVORITE_SLOT_COUNT).fill(null));
	isSavingFavorites = signal(false);

	/**
	 * Pool de cartes du picker favoris, paginé côté serveur (même endpoint que
	 * la grille inventaire) — au lieu de monter une `app-card` par carte
	 * possédée d'un coup (même souci de perf que la grille inventaire avant
	 * sa pagination).
	 */
	readonly favoritesPoolPageSize = 24;
	/** Taille des requêtes de chargement de tout le pool (puis filtré et paginé côté client). */
	private readonly favoritesPoolFetchSize = 100;
	favoritesPoolPage = signal(0);
	/** Toutes les cartes possédées (score > 0). L'API n'a pas de filtre sur le score : paginer côté serveur laissait des pages quasi vides une fois les cartes à 0 masquées. */
	private readonly favoritesPoolAll = signal<InventoryCard[]>([]);
	favoritesPoolTotalPages = computed(() =>
		Math.max(1, Math.ceil(this.favoritesPoolAll().length / this.favoritesPoolPageSize))
	);
	favoritesPoolCards = computed(() => {
		const start = this.favoritesPoolPage() * this.favoritesPoolPageSize;
		return this.favoritesPoolAll().slice(start, start + this.favoritesPoolPageSize);
	});
	isFavoritesPoolLoading = signal(false);

	private loadFavoritesPool(): void {
		this.isFavoritesPoolLoading.set(true);
		const fetchPage = (page: number) => this.inventoryService.getInventoryCards({
			page,
			size: this.favoritesPoolFetchSize,
			sortBy: 'rarity',
		});

		fetchPage(0).pipe(
			expand(res => res.number + 1 < res.totalPages ? fetchPage(res.number + 1) : EMPTY),
			reduce((acc, res) => acc.concat(res.content), [] as InventoryCard[]),
		).subscribe({
			next: (cards) => {
				this.favoritesPoolAll.set(cards.filter(c => c.score !== 0));
				// Si la liste a rétréci, reste sur une page valide.
				this.favoritesPoolPage.set(Math.min(this.favoritesPoolPage(), this.favoritesPoolTotalPages() - 1));
				this.isFavoritesPoolLoading.set(false);
			},
			error: (err) => {
				console.error('Failed to load favorites pool', err);
				this.isFavoritesPoolLoading.set(false);
			},
		});
	}

	onFavoritesPoolPageChange(page: number): void {
		this.favoritesPoolPage.set(page);
	}

	/** Exemplaires encore disponibles : un exemplaire en quantity N reste dans le pool tant qu'il n'a pas occupé N slots. */
	favoritePool = computed((): PoolCard[] => {
		const placedCount = new Map<string, number>();
		for (const slot of this.favoriteSlots()) {
			if (slot) placedCount.set(slot.userCardId, (placedCount.get(slot.userCardId) ?? 0) + 1);
		}
		return this.favoritesPoolCards()
			.map(entry => ({
				userCardId: entry.userCardId,
				card: entry.card,
				score: entry.score,
				remaining: entry.quantity - (placedCount.get(entry.userCardId) ?? 0),
			}))
			.filter(o => o.remaining > 0);
	});

	favoritesDirty = computed(() => {
		const current = this.favoriteSlots().map(o => o?.userCardId ?? null);
		const saved = this.savedFavoriteIds();
		return current.some((id, i) => id !== saved[i]);
	});

	canSaveFavorites = computed(() =>
		this.favoriteSlots().every(c => c !== null) && this.favoritesDirty()
	);

	/**
	 * Grille inventaire chargée par pages côté serveur (`GET /inventory/cards`),
	 * accumulées au scroll (infini) — borne le nombre de `app-card` montées d'un
	 * coup (chacune démarre des effets perpétuels dès qu'elle est visible) et
	 * évite de tout télécharger d'emblée. Un changement de filtre/tri repart de
	 * la page 0 en remplaçant la liste.
	 */
	readonly inventoryPageSize = 28;
	inventoryPage = signal(0);
	inventoryTotalPages = signal(1);
	pagedCards = signal<InventoryCard[]>([]);
	isInventoryCardsLoading = signal(false);
	isInventoryLoadingMore = signal(false);
	isShowNotOwnedCard = signal<boolean>(false);
	inventoryHasMore = computed(() => this.inventoryPage() + 1 < this.inventoryTotalPages());
	private nameFilterDebounce: ReturnType<typeof setTimeout> | null = null;

	// ── Scroll infini : observer sur la sentinelle en bas de grille ──
	private inventoryScrollObserver: IntersectionObserver | null = null;
	private inventoryLoadMoreEl: HTMLElement | null = null;

	@ViewChild('inventoryLoadMoreAnchor')
	set inventoryLoadMoreAnchor(ref: ElementRef<HTMLElement> | undefined) {
		this.inventoryScrollObserver?.disconnect();
		this.inventoryLoadMoreEl = ref?.nativeElement ?? null;
		if (this.inventoryLoadMoreEl) this.inventoryScrollObserver?.observe(this.inventoryLoadMoreEl);
	}

	/** Relance l'observation après un append : la sentinelle peut rester dans le viewport sans nouvelle transition. */
	private reobserveInventoryAnchor(): void {
		if (!this.inventoryScrollObserver || !this.inventoryLoadMoreEl) return;
		this.inventoryScrollObserver.disconnect();
		this.inventoryScrollObserver.observe(this.inventoryLoadMoreEl);
	}

	private onInventoryScrollNearEnd(): void {
		if (!this.inventoryHasMore() || this.isInventoryLoadingMore() || this.isInventoryCardsLoading()) return;
		this.inventoryPage.set(this.inventoryPage() + 1);
		this.loadInventoryCards(true);
	}

	private loadInventoryCards(append = false): void {
		if (append) this.isInventoryLoadingMore.set(true);
		else this.isInventoryCardsLoading.set(true);

		this.inventoryService.getInventoryCards({
			page: this.inventoryPage(),
			size: this.inventoryPageSize,
			sortBy: this.inventorySort(),
			direction: this.inventorySortDirection(),
			rarity: this.selectedRarity() ?? undefined,
			extensionId: this.selectedExtension() ?? undefined,
			name: this.cardNameFilter().trim() || undefined,
		}).subscribe({
			next: (res) => {
				this.pagedCards.set(append ? [...this.pagedCards(), ...res.content] : res.content);
				this.inventoryTotalPages.set(res.totalPages);
				this.isInventoryCardsLoading.set(false);
				this.isInventoryLoadingMore.set(false);
				if (append) setTimeout(() => this.reobserveInventoryAnchor());
			},
			error: (err) => {
				console.error('Failed to load inventory cards', err);
				this.isInventoryCardsLoading.set(false);
				this.isInventoryLoadingMore.set(false);
			},
		});
	}

	/** Repart de la page 0 (après mutation d'inventaire : ouverture booster, destruction, mise en vente). */
	private reloadInventoryFromStart(): void {
		this.inventoryPage.set(0);
		this.loadInventoryCards();
	}

	setRarityFilter(rarity: CardRarity | null): void {
		this.selectedRarity.set(rarity);
		this.inventoryPage.set(0);
		this.loadInventoryCards();
	}

	setExtensionFilter(extensionId: string | null): void {
		this.selectedExtension.set(extensionId);
		this.inventoryPage.set(0);
		this.loadInventoryCards();
	}

	setInventorySort(sort: 'rarity' | 'name' | 'score'): void {
		if (this.inventorySort() === sort) {
			return;
		}
		this.inventorySort.set(sort);
		this.inventoryPage.set(0);
		this.loadInventoryCards();
	}

	setInventorySortDirection(direction: 'asc' | 'desc'): void {
		if (this.inventorySortDirection() === direction) {
			return;
		}
		this.inventorySortDirection.set(direction);
		this.inventoryPage.set(0);
		this.loadInventoryCards();
	}

	/** Debounce 300ms pour ne pas taper une requête serveur à chaque frappe. */
	onNameFilterInput(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.cardNameFilter.set(value);
		if (this.nameFilterDebounce) clearTimeout(this.nameFilterDebounce);
		this.nameFilterDebounce = setTimeout(() => {
			this.inventoryPage.set(0);
			this.loadInventoryCards();
		}, 300);
	}

	page = 0;
	totalPages = 0;

	minioBaseUrl = environment.minioBaseUrl;

	ngOnInit(): void {
		// Charge les feature flags (dont MARKETPLACE) pour conditionner la mise en vente.
		this.featureFlagService.getFeatureFlags().subscribe();

		// Montants de destruction (édités depuis le back-office) — affichés dans la modal détail.
		this.cardService.getDestroyRewards().subscribe({
			next: (rewards) => this.destroyRewardByRarity.set(rewards),
			error: () => {},
		});

		this.loadExtensions();

		if (typeof IntersectionObserver !== 'undefined') {
			this.inventoryScrollObserver = new IntersectionObserver(
				(entries) => {
					if (entries.some(e => e.isIntersecting)) this.onInventoryScrollNearEnd();
				},
				{ rootMargin: '600px 0px' },
			);
		}

		this.activeRoute.queryParams.subscribe((params) => {
			if (params['discordLinked'] === 'true') {
				this.selectedTab.set('account');
				this.toastService.show(this.translate.instant('page.profile.account.discord-linked-success'), 'success', 5000);
				this.router.navigate([], {
					relativeTo: this.activeRoute,
					queryParams: { discordLinked: null },
					queryParamsHandling: 'merge',
					replaceUrl: true
				});
			}
		});

		if (this.authService.isAuthenticated()) {
			this.userService.getUserProfile().subscribe({
				next: (user) => {
					this.user.set(user);
					this.isLoading.set(false);
					if (user) {
						this.loadInventory();
						this.loadInventoryCards();
						this.loadFavoritesPool();
						this.loadPredictions();
						this.loadStatistics();
						this.loadFavorites();
						this.loadDiscordStatus();
					}
				},
				error: (err) => {
					console.error('Failed to load user profile', err);
					this.user.set(null);
					this.isLoading.set(false);
				}
			});
		}
	}

	private loadDiscordStatus(): void {
		this.discordService.getLinkStatus().subscribe({
			next: (status) => this.discordStatus.set(status),
			error: (err) => console.error('Failed to load discord link status', err),
		});
	}

	linkDiscord(): void {
		if (this.isLinkingDiscord()) return;
		this.isLinkingDiscord.set(true);
		this.discordService.getAuthorizeUrl().subscribe({
			next: (res) => {
				window.location.href = res.url;
			},
			error: (err) => {
				console.error('Failed to start discord linking', err);
				this.isLinkingDiscord.set(false);
				this.toastService.show(this.translate.instant('page.profile.account.discord-link-error'), 'error', 5000);
			}
		});
	}

	unlinkDiscord(): void {
		if (this.isUnlinkingDiscord()) return;
		this.isUnlinkingDiscord.set(true);
		this.discordService.unlink().subscribe({
			next: () => {
				this.isUnlinkingDiscord.set(false);
				this.discordStatus.set({ linked: false, discordUsername: null, discordAvatarUrl: null });
				this.toastService.show(this.translate.instant('page.profile.account.discord-unlink-success'), 'success', 5000);
			},
			error: (err) => {
				console.error('Failed to unlink discord', err);
				this.isUnlinkingDiscord.set(false);
				this.toastService.show(this.translate.instant('page.profile.account.discord-unlink-error'), 'error', 5000);
			}
		});
	}

	selectTab(tab:string) {
		this.selectedTab.set(tab);
	}

	private loadPredictions() {
		this.predictionService.getPredictionsByUserPaginated(this.page).subscribe({
			next: (data) => {
				this.predictions.set(data.content);
				this.totalPages = data.totalPages;
			},
			error: () => {}
		});
	}

	private loadStatistics() {
		this.statisticsService.getUserStatistics().subscribe({
			next: (data) => this.userStats.set(data),
			error: () => {}
		});
	}

	onPageChange(page: number) {
		this.page = page;
		this.loadPredictions();
	}

	deleteAccount() {
		this.userService.deleteAccount().subscribe({
			next: () => {
				this.isWantDeleteAccount.set(false);
				this.authService.deleteAccount();
				this.toastService.show(this.translate.instant('page.profile.delete-account.success'), 'success', 10000);
				this.router.navigate(['/']);
			},
			error: () => {
				this.toastService.show(this.translate.instant('page.profile.delete-account.error'), 'error', 10000);
			}
		});
	}

	navigateTo(path: string) {
		this.router.navigate([path]);
	}

	onBackgroundClick(event: Event) {
		if (event.target === event.currentTarget) {
			this.closeDeleteAccount();
		}
	}

	closeDeleteAccount() {
		this.isWantDeleteAccount.set(false);
	}

	/** Modal simple "voir en grand" — juste la carte, ouverte au clic sur la tuile. */
	openCardDetail(entry: InventoryCard): void {
		this.selectedCard.set({userCardId: entry.userCardId, card: entry.card, score: entry.score});
		document.body.style.overflow = 'hidden';
	}

	closeCardDetail(): void {
		this.selectedCard.set(null);
		document.body.style.overflow = '';
	}

	onCardDetailBackdropClick(event: Event): void {
		if (event.target === event.currentTarget) this.closeCardDetail();
	}

	/** Modal détail dédiée (bouton "œil") — historique des prix + détruire/vendre. */
	openCardDetailPanel(entry: InventoryCard): void {
		this.viewingCard.set(entry);
		document.body.style.overflow = 'hidden';
		this.isDestroyConfirming.set(false);
		this.isSellPriceOpen.set(false);
		this.sellPrice.set(50);
		this.requestSellCard();
		this.loadDetailSales(entry.card.id);
	}

	closeCardDetailPanel(): void {
		if (this.isDestroying() || this.isListingFromDetail()) return;
		this.viewingCard.set(null);
		document.body.style.overflow = '';
	}

	onCardDetailPanelBackdropClick(event: Event): void {
		if (event.target === event.currentTarget) this.closeCardDetailPanel();
	}

	private loadDetailSales(cardId: string): void {
		this.detailSales.set([]);
		this.detailSalesNotFound.set(false);
		this.isDetailSalesLoading.set(true);
		this.marketplaceService.getSales(cardId, 20).subscribe({
			next: (sales) => {
				// API renvoie DESC (plus récente en premier) ; le graphe veut l'ordre chronologique.
				this.detailSales.set(sales.slice().reverse());
				this.isDetailSalesLoading.set(false);
			},
			error: (err: HttpErrorResponse) => {
				this.isDetailSalesLoading.set(false);
				if (err.status === 404) this.detailSalesNotFound.set(true);
			},
		});
	}

	requestDestroyCard(entry: InventoryCard): void {
		this.viewingCard.set(entry);
		this.isDestroyConfirming.set(true);
	}

	cancelDestroyCard(): void {
		this.isDestroyConfirming.set(false);
		this.viewingCard.set(null);
	}

	confirmDestroyCard(): void {
		const entry = this.viewingCard();
		if (!entry || this.isDestroying()) return;
		this.isDestroying.set(true);
		this.cardService.destroyCard(entry.userCardId).subscribe({
			next: (result: CardDestroyResult) => {
				this.isDestroying.set(false);
				this.isDestroyConfirming.set(false);
				this.toastService.show(
					this.translate.instant('page.profile.inventory.detail.destroy-success', {coins: result.coinsEarned}),
					'success', 5000,
				);
				this.closeCardDetailPanel();
				this.loadInventory();
				this.reloadInventoryFromStart();
				this.refreshCoins();
			},
			error: (err: HttpErrorResponse) => {
				this.isDestroying.set(false);
				const message = err.error?.message;
				this.toastService.show(message || this.translate.instant('page.profile.inventory.detail.destroy-error'), 'error', 5000);
			},
		});
	}

	requestSellCard(): void {
		const entry = this.viewingCard();
		if (!entry || !this.featureFlagService.isStrictlyEnabled('MARKETPLACE')) return;
		this.sellPrice.set(50);
		this.isSellPriceOpen.set(true);
	}

	cancelSellCard(): void {
		this.isSellPriceOpen.set(false);
		this.viewingCard.set(null);
		document.body.style.overflow = '';
	}

	confirmSellFromDetail(): void {
		const entry = this.viewingCard();
		if (!entry || this.sellPrice() <= 0 || this.isListingFromDetail()) return;
		if (!this.featureFlagService.isStrictlyEnabled('MARKETPLACE')) {
			this.toastService.show(this.translate.instant('page.marketplace.maintenance.text'), 'error', 5000);
			return;
		}
		this.isListingFromDetail.set(true);
		this.marketplaceService.createListing({userCardId: entry.userCardId, price: this.sellPrice()}).subscribe({
			next: () => {
				this.isListingFromDetail.set(false);
				this.isSellPriceOpen.set(false);
				this.toastService.show(this.translate.instant('page.marketplace.sell-modal.success'), 'success', 5000);
				this.closeCardDetailPanel();
				this.loadInventory();
				this.reloadInventoryFromStart();
			},
			error: (err: HttpErrorResponse) => {
				this.isListingFromDetail.set(false);
				const message = err.error?.message;
				this.toastService.show(message || this.translate.instant('page.marketplace.sell-modal.error'), 'error', 5000);
			},
		});
	}

	ngOnDestroy(): void {
		// Sécurité : si le composant est détruit modal ouverte, on ne laisse pas le scroll bloqué.
		document.body.style.overflow = '';
		if (this.nameFilterDebounce) clearTimeout(this.nameFilterDebounce);
		this.inventoryScrollObserver?.disconnect();
	}

	boosterTooltipHtml(raritiesAllowed: string[]): string {
		const header = this.translate.instant('page.profile.inventory.rarities-tooltip');
		const rows = raritiesAllowed.map(rarity => {
			const option = this.rarityOptions.find(o => o.key === rarity);
			const color = option?.color ?? '#9ca3af';
			const label = option ? this.translate.instant(option.labelKey) : rarity;
			return `<div style="display:flex;align-items:center;gap:8px;margin-top:6px">
				<span style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;box-shadow:0 0 5px ${color}66"></span>
				<span style="color:${color};letter-spacing:0.15em;text-transform:uppercase;font-size:9px">${label}</span>
			</div>`;
		}).join('');
		return `<div style="font-size:9px;letter-spacing:0.2em;text-transform:uppercase;color:rgba(255,255,255,0.4)">${header}</div>${rows}`;
	}

	/** true si l'inventaire contient encore au moins un booster à ouvrir. */
	hasBoostersToOpen = computed(() => (this.inventory()?.boosters ?? []).some(b => b.quantity > 0));

	/** Image dédiée du booster, ou null (→ modèle 3D par défaut). */
	boosterImage = boosterImageOrNull;

	openBooster(boosterId: string): void {
		if (this.isOpeningBooster()) return;
		this.lastOpenedBoosterId = boosterId;
		this.isOpeningBooster.set(true);
		this.boosterService.openBooster(boosterId).subscribe({
			next: (cards) => {
				this.boosterStateService.setCards(cards);
				this.boosterStateService.open();
				this.isOpeningBooster.set(false);
				this.loadInventory();
				this.reloadInventoryFromStart();
			},
			error: () => {
				this.isOpeningBooster.set(false);
			},
		});
	}

	/** Depuis l'écran d'ouverture : enchaîne sur le même type de booster s'il en reste, sinon sur le prochain type disponible. */
	openNextBooster(): void {
		const boosters = this.inventory()?.boosters ?? [];
		const next = boosters.find(b => b.booster.id === this.lastOpenedBoosterId && b.quantity > 0)
			?? boosters.find(b => b.quantity > 0);
		if (next) this.openBooster(next.booster.id);
	}

	/** Recharge le solde PepsCoins (signal partagé AuthService + signal local) après un gain/dépense. */
	private refreshCoins(): void {
		this.authService.loadUser().subscribe({
			next: (user) => this.user.set(user),
			error: () => {},
		});
	}

	private loadInventory() {
		this.inventoryService.getInventory().subscribe({
			next: (data) => this.inventory.set(data),
			error: () => {},
		});
		this.cardService.getCardStats().subscribe({
			next: (data) => this.cardStats.set(data),
			error: () => {},
		});
	}

	private loadFavorites(): void {
		this.cardService.getFavorites().subscribe({
			next: (favorites) => this.applyFavorites(favorites),
			error: () => {},
		});
	}

	private applyFavorites(favorites: FavoriteCard[]): void {
		const slots: (OwnedCard | null)[] = new Array(FAVORITE_SLOT_COUNT).fill(null);
		for (const favorite of favorites) {
			if (favorite.position >= 0 && favorite.position < FAVORITE_SLOT_COUNT) {
				slots[favorite.position] = {userCardId: favorite.userCardId, card: favorite.card, score: favorite.score};
			}
		}
		this.favoriteSlots.set(slots);
		this.savedFavoriteIds.set(slots.map(o => o?.userCardId ?? null));
	}

	/** Place la carte glissée sur le slot cible, en échangeant avec l'occupant existant si besoin. */
	onFavoriteSlotDrop(event: CdkDragDrop<(OwnedCard | null)[]>, targetIndex: number): void {
		const dragged = this.toOwnedCard(event.item.data as OwnedCard);
		const current = [...this.favoriteSlots()];
		// Source = slot d'origine du drag (et non une recherche par userCardId : le même exemplaire
		// peut occuper plusieurs slots). -1 si la carte vient du pool.
		const sourceIndex = this.favoriteSlotListIds.indexOf(event.previousContainer.id);
		if (sourceIndex === targetIndex) return;

		const displaced = current[targetIndex];
		current[targetIndex] = dragged;
		if (sourceIndex !== -1) {
			current[sourceIndex] = displaced ?? null;
		}
		this.favoriteSlots.set(current);
	}

	/** Retire la carte glissée d'un slot lorsqu'elle est déposée dans le pool. */
	onFavoritePoolDrop(event: CdkDragDrop<PoolCard[]>): void {
		const current = [...this.favoriteSlots()];
		const sourceIndex = this.favoriteSlotListIds.indexOf(event.previousContainer.id);
		if (sourceIndex === -1) return;

		current[sourceIndex] = null;
		this.favoriteSlots.set(current);
	}

	/** Retire les champs propres au pool (remaining) : un slot ne porte que userCardId + carte + score. */
	private toOwnedCard(o: OwnedCard): OwnedCard {
		return {userCardId: o.userCardId, card: o.card, score: o.score};
	}

	addFavoriteToFirstEmptySlot(owned: OwnedCard): void {
		const current = [...this.favoriteSlots()];
		const emptyIndex = current.findIndex(o => o === null);
		if (emptyIndex === -1) return;

		current[emptyIndex] = this.toOwnedCard(owned);
		this.favoriteSlots.set(current);
	}

	removeFavoriteSlot(index: number): void {
		const current = [...this.favoriteSlots()];
		current[index] = null;
		this.favoriteSlots.set(current);
	}

	resetFavorites(): void {
		const owned = this.inventory()?.cards ?? [];
		const slots = this.savedFavoriteIds().map(id => {
			if (!id) return null;
			const entry = owned.find(e => e.userCardId === id);
			return entry ? {userCardId: entry.userCardId, card: entry.card, score: entry.score} : null;
		});
		this.favoriteSlots.set(slots);
	}

	saveFavorites(): void {
		if (!this.canSaveFavorites() || this.isSavingFavorites()) return;
		const userCardIds = this.favoriteSlots().map(o => o!.userCardId);
		this.isSavingFavorites.set(true);
		this.cardService.setFavorites(userCardIds).subscribe({
			next: (favorites) => {
				this.applyFavorites(favorites);
				this.isSavingFavorites.set(false);
				this.toastService.show(this.translate.instant('page.profile.favorites.success'), 'success', 5000);
			},
			error: (err: HttpErrorResponse) => {
				this.isSavingFavorites.set(false);
				const message = err.error?.message;
				this.toastService.show(message || this.translate.instant('page.profile.favorites.error'), 'error', 5000);
			},
		});
	}

	private loadExtensions() {
		this.extensionService.getAllExtensions().subscribe({
			next: (extensions) => this.extensions.set(extensions),
			error: (err) => {
				console.error('Failed to load extensions', err);
			}
		});
	}

	protected onDestroyModalBackdropClick(event: Event): void {
		if (event.target === event.currentTarget) this.cancelDestroyCard();
	}

	protected toggleShowNotOwned() {
		this.isShowNotOwnedCard.set(!this.isShowNotOwnedCard());
	}
}
